import React, { useEffect, useState, useContext } from "react";
import {
  Table,
  Spinner,
  Alert,
  Container,
  Card,
  Button,
  Modal,
  Col,
  Row,
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";
import { getDevicesSummary, toggleDevices } from "./deviceService";
import HusariaHeader from "views/pages/components/HusariaHeader";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";

const DevicesTable = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [toggleModal, setToggleModal] = useState(false);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [devicesSummary, setDevicesSummary] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [profiles, setProfiles] = useState([]);

  const visibleColumns = [
    { key: "device_address", label: "Address" },
    { key: "device_label", label: "Label" },
    { key: "device_vendor", label: "Vendor" },
    { key: "device_template", label: "Template" },
    { key: "device_profile_uuid", label: "Profiles" },
    { key: "device_enabled", label: "Enabled" },
    { key: "device_status", label: "Status" },
    { key: "device_description", label: "Description" },
  ];

  const booleanColumns = ["device_enabled"];

  const formatBoolean = (val) => {
    return val === true || val === "t" || val === "true" ? "Yes" : "No";
  };

  const info_style = {
    backgroundColor: isDark ? "#1f2937" : "#fff",
    borderRadius: "1rem",
    overflow: "hidden",
  }

  const userData = React.useMemo(() => {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  }, []);

  const canCreate = hasPermission(userData, PERMISSIONS.CREATE);
  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
  const canDelete = hasPermission(userData, PERMISSIONS.DELETE);

  const canPerformActions = canCreate || canEdit || canDelete;

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      if (booleanColumns.includes(col.key)) {
        return formatBoolean(cell);
      }

      if (col.key === "device_profile_uuid") {
        return profileNameByUuid[cell] || "-";
      }

      if (index === 0) {
        return canEdit ? (
          <span
            className="tr-row-name"
            onClick={() =>
              navigate(`/admin/hucs/edit/device/${row.device_uuid}`)
            }
            style={{
              cursor: "pointer",
              color: isDark ? "#60a5fa" : "#0d6efd",
            }}
          >
            {cell ?? "-"}
          </span>
        ) : (
          <span style={{ fontWeight: 500 }}>{cell ?? "-"}</span>
        );
      }

      return cell ?? "-";
    },
  }));

  if (canPerformActions) {
    columns.push({
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) =>
        canDelete ? (
          <Button
            size="sm"
            variant="danger"
            onClick={(e) => {
              e.stopPropagation();
              toggleDeleteModal(row);
            }}
          >
            Delete
          </Button>
        ) : null,
    });
  }

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    selected: selectedRows,
    onSelect: (row, isSelect) => {
      setSelectedRows(prev =>
        isSelect
          ? [...prev, row.device_uuid]
          : prev.filter(id => id !== row.device_uuid)
      );
    },
    onSelectAll: (isSelect, rows) => {
      if (isSelect) {
        setSelectedRows(rows.map(r => r.device_uuid));
      } else {
        setSelectedRows([]);
      }
    },
    classes: "checkbox-column",
    headerClasses: "checkbox-column-header",
  };

  React.useEffect(() => {
    const loadSummary = async () => {
      try {
        const site = JSON.parse(localStorage.getItem("selectedSite"));
        const summary = await getDevicesSummary(site?.site_uuid);
        setDevicesSummary(summary);
      } catch (err) {
        console.error(err);
      }
    };

    loadSummary();
  }, []);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
      const site_uuid = currentSite?.site_uuid || null;
      const res = await fetch(
        `${serverHost}/get_rows?table=v_devices&pageSize=50&page=1&domain_uuid=${site_uuid}`
      );
      if (!res.ok) throw new Error("Failed to load devices");
      const data = await res.json();
      setDevices(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
        const domain_uuid = currentSite?.domain_uuid || currentSite?.site_uuid;
        if (!domain_uuid) return;

        const res = await fetch(
          `${serverHost}/get_rows?table=v_device_profiles&pageSize=1000&page=1&domain_uuid=${domain_uuid}`
        );
        if (!res.ok) throw new Error("Failed to load device profiles");

        const data = await res.json();
        setProfiles(data.results || []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchProfiles();
  }, []);

  const profileNameByUuid = React.useMemo(() => {
    const map = {};
    for (const p of profiles) {
      map[p.device_profile_uuid] = p.device_profile_name || p.device_profile_uuid;
    }
    return map;
  }, [profiles]);

  const toggleDeleteModal = (device = null) => {
    setDeviceToDelete(device);
    setDeleteModal(!deleteModal);
  };

  const toggleToggleModal = () => {
    setToggleModal(!toggleModal);
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // "asc" | "desc"
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const filteredRows = React.useMemo(() => {
    if (!search) return devices;
    const s = search.toLowerCase();

    return devices.filter((row) =>
      visibleColumns.some((col) => {
        const raw =
          col.key === "device_profile_uuid"
            ? profileNameByUuid[row.device_profile_uuid]
            : row[col.key];

        return String(raw ?? "").toLowerCase().includes(s);
      })
    );
  }, [devices, search, visibleColumns, profileNameByUuid]);

  /* ------------------ DELETE DEVICE ------------------ */

  const handleDelete = async () => {
    try {
      let deviceUuids = [];

      // Single delete
      if (deviceToDelete?.device_uuid) {
        deviceUuids = [deviceToDelete.device_uuid];
      }
      // Bulk delete
      else if (selectedRows.length > 0) {
        deviceUuids = selectedRows;
      }
      else {
        throw new Error("No devices selected for deletion");
      }

      // Delete sequentially
      for (const uuid of deviceUuids) {
        const res = await fetch(`${serverHost}/delete`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            table: "v_devices",
            where: { device_uuid: uuid },
          }),
        });

        if (!res.ok) {
          throw new Error(`Failed to delete device ${uuid}`);
        }

        await res.json();
      }

      // Cleanup UI state
      toggleDeleteModal();
      setDeviceToDelete(null);
      setSelectedRows([]);
      fetchDevices();
    } catch (err) {
      alert(`Error deleting device(s): ${err.message}`);
    }
  };


  /* ------------------ HANDLE TOGGLE ------------------ */
  const handleToggle = async () => {
    if (selectedRows.length === 0) return;
    setSelectedRows([]);

    toggleToggleModal();

    try {
      await toggleDevices(selectedRows);
      await fetchDevices();
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      const summary = await getDevicesSummary(site?.site_uuid);
      setDevicesSummary(summary);
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading devices...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <HusariaHeader name="Devices" parentName="HUCS" padding="9rem" />

      <Container fluid className="mt--8">
        {/* ===================== STATISTICS CARDS ===================== */}
        <Row className="mb-4">
          <Col lg="4" md="4" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0"
                      style={{
                        fontSize: "0.875rem",
                        color: isDark ? "#adb5bd" : "#6c757d",
                        fontWeight: 500,
                      }}
                    >
                      Number of Devices
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700 }}>
                      {devicesSummary.devices ?? 0}
                    </h2>
                  </div>
                  <i
                    className="fas fa-laptop"
                    style={{ fontSize: "2.5rem" }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg="4" md="4" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0"
                      style={{
                        fontSize: "0.875rem",
                        color: isDark ? "#adb5bd" : "#6c757d",
                        fontWeight: 500,
                      }}
                    >
                      Enabled Devices
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700, color: "#28a745" }}>
                      {devicesSummary.devices - (devicesSummary.disabled ?? 0)}
                    </h2>
                  </div>

                  <i
                    className="fas fa-check-circle text-success"
                    style={{ fontSize: "2.5rem" }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg="4" md="4" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0"
                      style={{
                        fontSize: "0.875rem",
                        color: isDark ? "#adb5bd" : "#6c757d",
                        fontWeight: 500,
                      }}
                    >
                      Disabled Devices
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700, color: "#c61b1b" }}>
                      {devicesSummary.disabled ?? 0}
                    </h2>
                  </div>

                  <i
                    className={`fas fa-times-circle ${(devicesSummary.disabled ?? 0) === 0
                      ? "text-success"
                      : "text-warning"
                      }`}
                    style={{ fontSize: "2.5rem" }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
            className="d-flex justify-content-between align-items-center"
          >
            {/* LEFT: Title + Search */}
            <div className="d-flex align-items-center">
              <form className="navbar-search navbar-search-light form-inline">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge search-border">
                    <InputGroupText>
                      <i className="fas fa-search search-icon" />
                    </InputGroupText>
                    <Input
                      className="form-control table-search-bar"
                      placeholder="Search devices"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ width: "220px" }}
                    />
                  </InputGroup>
                </FormGroup>
              </form>
            </div>

            {/* RIGHT: Actions */}
            <div className="d-flex gap-2">

              {canEdit && selectedRows.length > 0 && (
                <Button
                  size="sm"
                  variant="info"
                  onClick={() => setToggleModal(true)}
                >
                  Toggle Enabled ({selectedRows.length})
                </Button>
              )}

              {canDelete && selectedRows.length > 0 && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setDeviceToDelete(null);
                    setDeleteModal(true);
                  }}
                >
                  Delete Selected ({selectedRows.length})
                </Button>
              )}

              {hasPermission(userData, PERMISSIONS.CREATE) && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate("/admin/hucs/add/device")}
                >
                  + Add Device
                </Button>
              )}
            </div>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            <div style={{ overflowX: "auto" }}>
              <ToolkitProvider
                keyField="device_uuid"
                data={filteredRows}
                columns={columns}
                search
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    <BootstrapTable
                      {...props.baseProps}
                      bootstrap4
                      pagination={pagination}
                      bordered={false}
                      selectRow={canDelete ? selectRow : undefined}
                      hover
                      rowStyle={{ cursor: "pointer" }}
                      classes="align-items-center table-flush"
                      rowEvents={{
                        onClick: (_, row) => {
                          if (canEdit) {
                            navigate(`/admin/hucs/edit/device/${row.device_uuid}`);
                          }
                        },
                      }}
                      noDataIndication={() =>
                        loading ? (
                          <div className="text-center">Loading...</div>
                        ) : (
                          <div className="text-center">No devices found.</div>
                        )
                      }
                    />
                  </div>
                )}
              </ToolkitProvider>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* ===================== TOGGLE ENABLED MODAL ===================== */}
      <Modal show={toggleModal} onHide={() => setToggleModal(false)}>
        <Modal.Header closeButton style={{ backgroundColor: isDark ? "#1f2937" : "#f8f9fa" }}>
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            {selectedRows.length === 1 ? "Toggle Device" : "Toggle Devices"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: isDark ? "#111827" : "#fff", color: isDark ? "#e2e8f0" : "#212529" }}>
          Are you sure you want to toggle{" "}
          <strong>
            {selectedRows.length === 1 ? "this device" : `${selectedRows.length} devices`}
          </strong>?
        </Modal.Body>

        <Modal.Footer style={{ backgroundColor: isDark ? "#1f2937" : "#f8f9fa" }}>
          <Button variant="light" onClick={toggleToggleModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleToggle}>
            Toggle Enabled
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===================== DELETE MODAL ===================== */}

      <Modal show={deleteModal} onHide={toggleDeleteModal}>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            {deviceToDelete || selectedRows.length === 1
              ? "Delete Device"
              : "Delete Devices"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDark ? "#111827" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          {deviceToDelete || selectedRows.length === 1 ? (
            <>
              Are you sure you want to delete this device?
              <br />
              <strong>This action cannot be undone.</strong>
            </>
          ) : (
            <>
              Are you sure you want to delete{" "}
              <strong>{selectedRows.length}</strong> devices?
              <br />
              <strong>This action cannot be undone.</strong>
            </>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
          }}
        >
          <Button variant="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DevicesTable;
